import { Collapse, Grid } from "@mui/material";
import { InfoService } from "../../APIService/InfoService";
import LearningTable from "../../components/reportTables/LearningTable";
import { authObj, useAuthContext } from "../../context/authContext";
import {
  setDocumentTitle,
  useSiteNameContext,
} from "../../context/siteNameContext";
import Loading from "../../shared/loading";
import { TAssignedCoursesTableType, TUserInfo } from "../../types";
import { useEffect, useState } from "react";
import { StyledInput } from "../../components/login/StyledInput";
import { Button } from "../../shared/button";
import { useParams } from "react-router-dom";
import { isAdmin, isDataOp, isNoRoles, isTeacher } from "../../helpers/role";
import { useSnackbar } from "notistack";
import { UserService } from "../../APIService/UserService";
import TaskService from "../../APIService/TaskService";
import { CourseTypeEnum } from "../../enums";

function AssignForm(props: {
  user_id: any;
  assign: (userId: number, something: string) => Promise<boolean>;
  label: string;
  buttonLabel: string;
  setWasChanged: (value: boolean) => void;
}) {
  const [something, setSomething] = useState("");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <StyledInput
        size="small"
        label={props.label}
        value={something}
        onChange={(e: any) => {
          if (RegExp("[^0-9, ]", "i").exec(e.target.value)) return;
          setSomething(e.target.value);
          props.setWasChanged(true);
        }}
      />
      <Button
        onClick={async () => {
          if (await props.assign(props.user_id, something)) {
            setSomething("");
          }
          props.setWasChanged(false);
        }}
      >
        {props.buttonLabel}
      </Button>
    </div>
  );
}

export function LearningPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { siteNameList } = useSiteNameContext();
  setDocumentTitle(siteNameList, "report9");

  const { authInfo } = useAuthContext();
  const [open, setOpen] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(true);
  const [userInfo, setUserInfo] = useState<TUserInfo>({} as TUserInfo);
  const [loading, setLoading] = useState<boolean>(true);
  const [result, setResult] = useState([] as TAssignedCoursesTableType[]);
  const [secondAssignAttempt, setSecondAssignAttempt] = useState(false);

  const params = useParams();
  const id = params.id ? Number(params.id) : authObj().id;

  useEffect(() => {
    UserService.getUserInfo(id).then((user) => {
      setUserInfo(user);
    });
  }, []);

  const [wasChangedCourseId, setWasChangedCourseId] = useState(false);
  const assignHomework = async (userId: number, coursesList: string) => {
    const isInputValid = async (list: string) => {
      let uniques = new Set<string>();
      list.split(",").forEach((x) => {
        if (x.trim().length > 0) uniques.add(x.trim());
      });
      if (list.split(",").length != uniques.size) return false;

      // try to fetch all courses to check if they exist
      let valid = true;
      for (const courseId of uniques) {
        try {
          await TaskService.getCourse(courseId, CourseTypeEnum.COURSE);
        } catch (e) {
          valid = false;
          break;
        }
      }
      return valid;
    };

    if (coursesList.length == 0) {
      return true;
    }

    if (!(await isInputValid(coursesList))) {
      enqueueSnackbar(
        `Проверьте правильность ввода (дубли, несуществующие курсы)`,
        {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        },
      );
      return false;
    }

    const alreadyAssigned = await InfoService.getAssignedCourses(userId);
    let courses = coursesList.split(",");

    let assignNow = true;
    courses.forEach(async (x) => {
      if (
        alreadyAssigned.data.includes(Number(x)) &&
        (!secondAssignAttempt || wasChangedCourseId)
      ) {
        enqueueSnackbar(
          `Курс ${x} был назначен ранее, если вы хотите повторно назначить курс, нажмите кнопку еще раз`,
          {
            variant: "warning",
            anchorOrigin: { horizontal: "right", vertical: "top" },
          },
        );
        assignNow = false;
      }
    });

    if (assignNow) {
      for (const courseId of courses) {
        try {
          await InfoService.assignCourse(userId, courseId.trim());
          enqueueSnackbar(`Курс ${courseId} назначен ученику ${userId}`, {
            variant: "success",
            anchorOrigin: { horizontal: "right", vertical: "top" },
          });
        } catch (e) {
          enqueueSnackbar(`Ошибка назначения курса ${courseId}`, {
            variant: "error",
            anchorOrigin: { horizontal: "right", vertical: "top" },
          });
        }
      }
      setSecondAssignAttempt(false);
      setUpdateTrigger(!updateTrigger);
    } else {
      setSecondAssignAttempt(true);
    }
    return assignNow;
  };

  useEffect(() => {
    let days = 10000;
    if (isNoRoles(authInfo.role)) {
      days = 180;
    }
    InfoService.getLearningInfo(id.toString(), days).then((res) => {
      setResult(res.data as TAssignedCoursesTableType[]);
      setLoading(false);
    });
  }, [authInfo, updateTrigger, id]);

  const expandedRowContent = (row: any) => {
    const deleteCourse = async (userId: number, courseId: string) => {
      await InfoService.deleteCourse(userId, courseId);
      setUpdateTrigger(!updateTrigger);
      enqueueSnackbar(`Курс ${courseId} удален`, {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    };

    const hasAnswers = row.course_task_cnt - row.no_answer_cnt !== 0;
    const userCourse =
      row.assigner_id == id ? <div>Курс добавлен пользователем.</div> : <></>;

    return hasAnswers ? (
      <>
        {userCourse}
        <div>На задачи этого курса были даны ответы.</div>
        {isAdmin(authInfo.role) ? (
          <Button onClick={() => deleteCourse(id, row.course_id)}>
            Удалить
          </Button>
        ) : (
          <></>
        )}
      </>
    ) : (
      <>
        {userCourse}
        <Button onClick={() => deleteCourse(id, row.course_id)}>Удалить</Button>
      </>
    );
  };

  const forAdminsAndTeachers =
    isAdmin(authInfo.role) || isTeacher(authInfo.role);

  return (
    <div className="Wrapper">
      {loading ? (
        <Loading />
      ) : (
        <>
          <h2>
            Отчет 9. Список курсов ученика {id}{" "}
            {isAdmin(authInfo.role) || isTeacher(authInfo.role)
              ? " (" + userInfo?.sysName + ")"
              : ""}
          </h2>

          {result?.length ? (
            <>
              <Grid item xs={12} sm={4} md={2}>
                {isAdmin(authInfo.role) ||
                isTeacher(authInfo.role) ||
                isDataOp(authInfo.role) ? (
                  <Button
                    aria-label="expand actions"
                    style={{ marginBottom: "1rem", marginLeft: "1rem" }}
                    onClick={() => setOpen(!open)}
                  >
                    {open ? "Скрыть" : "Действия"}
                  </Button>
                ) : (
                  <></>
                )}
              </Grid>

              <Collapse in={open} timeout="auto" unmountOnExit>
                <div>
                  {isAdmin(authInfo.role) ||
                  isTeacher(authInfo.role) ||
                  isDataOp(authInfo.role) ? (
                    <AssignForm
                      user_id={id}
                      assign={assignHomework}
                      setWasChanged={setWasChangedCourseId}
                      label={"Список курсов через запятую"}
                      buttonLabel={"Назначить ДЗ"}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </Collapse>

              <LearningTable
                userId={id}
                tableData={result}
                expandableRow={forAdminsAndTeachers}
                expandedRowContent={expandedRowContent}
                refreshData={() => {
                  setUpdateTrigger(!updateTrigger);
                }}
              />
            </>
          ) : (
            <>
              <p>Отсутствуют назначенные на Вас курсы!</p>
              {isAdmin(authInfo.role) ||
              isTeacher(authInfo.role) ||
              isDataOp(authInfo.role) ? (
                <AssignForm
                  user_id={id}
                  assign={assignHomework}
                  setWasChanged={setWasChangedCourseId}
                  label={"Список курсов через запятую"}
                  buttonLabel={"Назначить ДЗ"}
                />
              ) : (
                <></>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
